'use client';

import React, { useEffect, useRef } from 'react';
import type { CreateFlow } from '@/utils/analytics-events';
import { FORMAT_COPY } from '@/lib/create-flow/format-copy';
import { FormatIcon } from './FormatIcon';

const FOCUS_RING =
  'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-marigold-deep';

/** The format card (photo step) or the compact strip (later steps): makes the chosen format unmistakable. */
export function FormatHeader({
  format,
  variant,
  onSwitch,
  note,
  focusOnMount,
}: {
  format: CreateFlow;
  variant: 'card' | 'strip';
  onSwitch?: () => void;
  note?: string | null;
  focusOnMount?: boolean;
}) {
  const copy = FORMAT_COPY[format];
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (variant === 'card' && focusOnMount) headingRef.current?.focus();
  }, [variant, focusOnMount]);

  if (variant === 'strip') {
    return (
      <div className="sticky top-[calc(4rem+env(safe-area-inset-top))] z-30 min-[1000px]:static flex min-h-11 flex-wrap items-center gap-x-2 rounded-lg bg-purple-50 px-3 py-1.5 text-sm">
        <FormatIcon format={format} className="w-6 h-6 shrink-0 text-purple-700" />
        <span className="min-w-0 break-words text-gray-800">
          You&apos;re making a <strong className="uppercase tracking-wide text-purple-700">{copy.word}</strong>
          <span className="hidden sm:inline">: {copy.stripLine}</span>
        </span>
        {onSwitch && (
          <>
            <span aria-hidden="true" className="hidden min-[1000px]:inline">·</span>
            <button
              type="button"
              onClick={onSwitch}
              className={`min-h-11 shrink-0 font-semibold underline underline-offset-4 ${FOCUS_RING}`}
            >
              {copy.switchAsk}
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <section aria-labelledby="format-card-heading" className="rounded-xl border-2 border-purple-200 bg-white p-4 sm:p-6">
      <div className="flex items-start gap-4">
        <FormatIcon format={format} className="w-14 h-14 shrink-0 text-purple-700" />
        <div className="min-w-0">
          <h2
            id="format-card-heading"
            ref={headingRef}
            tabIndex={-1}
            className="text-2xl sm:text-3xl font-bold text-gray-900 focus:outline-none"
          >
            You&apos;re making a <strong className="uppercase tracking-wide text-purple-700">{copy.word}</strong>
          </h2>
          <p className="mt-1 text-gray-700">{copy.cardLine}</p>
          {note && <p className="mt-2 text-sm text-gray-600">{note}</p>}
          {onSwitch && (
            <button
              type="button"
              onClick={onSwitch}
              className={`mt-2 min-h-11 font-semibold text-purple-700 underline underline-offset-4 ${FOCUS_RING}`}
            >
              {copy.switchAsk}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
